/**
 * Global runtime safety net.
 *
 * The CRA dev-server overlay shows "Uncaught runtime errors" whenever an
 * unhandled promise rejection bubbles up. Many widgets (My Day, Dashboard,
 * Sidebars) fire axios calls with `.then(...)` and no `.catch(...)`. If any
 * of those endpoints transiently 404/5xx (or auth lapses), the overlay
 * appears on top of the entire app and blocks all interaction.
 *
 * This file silently swallows benign axios rejections (404 / 500 / network
 * errors) so they never reach the global overlay. Real, throwing JS errors
 * are still surfaced via the React ErrorBoundary in App.jsx.
 */

function isBenignAxiosRejection(reason) {
  if (!reason) return false;
  // axios errors carry `isAxiosError` + a response object
  if (reason.isAxiosError) return true;
  if (reason.code === "ERR_NETWORK" || reason.code === "ECONNABORTED") return true;
  // generic fetch / TypeError on network failure
  if (reason.name === "TypeError" && /fetch|NetworkError|Failed to fetch/i.test(String(reason.message))) {
    return true;
  }
  return false;
}

export function installRuntimeSafety() {
  if (typeof window === "undefined") return;
  if (window.__hassanos_runtime_safety_installed) return;
  window.__hassanos_runtime_safety_installed = true;

  // 1) Swallow unhandled axios / fetch promise rejections so the CRA
  //    dev-overlay never appears for transient network noise.
  window.addEventListener("unhandledrejection", (ev) => {
    if (isBenignAxiosRejection(ev.reason)) {
      // Log to console so devs can still see it, but stop the overlay.
      // eslint-disable-next-line no-console
      console.warn("[net] swallowed:", ev.reason?.config?.url || ev.reason?.message || ev.reason);
      ev.preventDefault();
    }
  });

  // 2) Hide the CRA error overlay if it ever mounts.
  //    The overlay is injected as <iframe> inside <body> by react-error-overlay.
  //    Use a MutationObserver so late-mounted overlays (e.g. lazy chunk
  //    failures mid-session) are caught instantly — not waiting for polls.
  const isOverlayIframe = (node) => {
    if (!(node instanceof HTMLElement)) return false;
    if (node.tagName !== "IFRAME") return false;
    const z = Number(node.style?.zIndex || 0);
    return node.style?.position === "fixed" && z > 1000;
  };
  const hideIfOverlay = (node) => {
    if (isOverlayIframe(node)) node.style.display = "none";
  };

  // Sweep anything already in the DOM.
  document.querySelectorAll("body > iframe").forEach(hideIfOverlay);

  // Watch <body> for any future iframe injection.
  if (typeof MutationObserver !== "undefined" && document.body) {
    const obs = new MutationObserver((records) => {
      for (const r of records) {
        r.addedNodes.forEach(hideIfOverlay);
      }
    });
    obs.observe(document.body, { childList: true });
  }
}
